/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Utilities for date and time
 *
 * @file DateTimeUtils.js
 * @author Satyaki Bose
 */

import { DateTime } from 'luxon';
import { getTimeZones } from '@vvo/tzdb';
import { TIMEFIELD_CONSTANTS } from './constants';
/** Picks first H:MM + am/pm anywhere in the string; ignores text before/after */
const TWELVE_HOUR_IN_STRING = /(\d{1,2}):(\d{2})\s*(a\.?\s*m\.?|p\.?\s*m\.?)/i;

const {
  DEFAULT_HOUR,
  DEFAULT_MINUTE,
  DEFAULT_PERIOD,
  DEFAULT_FORM_TIME,
  PERIOD_VALUES,
  HOUR_MIN,
  HOUR_MAX,
  MINUTE_MIN,
  MINUTE_MAX,
  DEFAULT_HOUR_DISPLAY,
  DEFAULT_MINUTE_DISPLAY
} = TIMEFIELD_CONSTANTS;

/** the npm package gives us offset in minutes, we convert to hours and minutes
 * eg:  330 to 5:30
 * */
const toOffsetLabel = (rawOffsetInMinutes) => {
  const sign = rawOffsetInMinutes >= 0 ? '+' : '-';
  const absMin = Math.abs(rawOffsetInMinutes);
  const h = String(Math.floor(absMin / 60)).padStart(2, '0');
  const m = absMin % 60;

  return m > 0 ? `UTC${sign}${h}:${String(m).padStart(2, '0')}` : `UTC${sign}${h}`;
};

/** Get DST-aware short abbreviation using Intl API */
const getAbbr = (ianaZone, referenceDate = new Date(), tz = {}) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaZone,
      timeZoneName: 'short'
    }).formatToParts(referenceDate);
    const abbr = parts.find(p => p.type === 'timeZoneName')?.value;

    // Fallback: if Intl gives something like "GMT+05:30", use tz.abbreviation instead
    // For example IST
    if (!abbr || abbr.startsWith('GMT')) return tz.abbreviation || abbr;

    return abbr;
  } catch {
    return tz.abbreviation || '';
  }
};

/**
 * Builds timezone options from @vvo/tzdb — curated abbreviations (e.g. "IST"), sorted UTC-12 to UTC+12.
 * need to group together since same offset but different name
 * TODO:: as of now no exact alternative of LMS , so this is the nearest we can get
 * Cached at module level — computed once per session.
 *
 * @example buildTimeZoneOptions() → [{ value: "Asia/Kolkata", name: "India Standard Time", abbr: "IST", offset: "UTC+05:30" }, ...]
 */
let tzOptionsCache = null;
export const buildTimeZoneOptions = () => {
  if (tzOptionsCache) return tzOptionsCache;
  const now = new Date(); // always respect current zone

  tzOptionsCache = getTimeZones({ includeUtc: true })
    .sort((a, b) => a.rawOffsetInMinutes - b.rawOffsetInMinutes)
    .map((tz) => {
      // DST-aware abbreviation
      const abbr = getAbbr(tz.name, now, tz);

      return {
        value: tz.name,
        name: tz.name, // alternative names can be ambiguous, like CST for Centar Time and Cuban Time
        group: tz.group ? [...tz.group] : [],
        abbr,
        offset: toOffsetLabel(tz.rawOffsetInMinutes)
      };
    });

  return tzOptionsCache;
};

export const parseDate = (dateStr) => {
  if (!dateStr) return null;
  // issue was when yyyy-mm-dd is parsed, JS converts to midnight UTC, not local
  // fix is to change to slashed version while converting to take the localDate
  const localDateStr = dateStr.replace(/-/g, '/');
  const date = new Date(localDateStr);

  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateToStorage = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const formatMonthYear = (date) => {
  if (!date) return '';

  return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

export const parse24Hour = (time24) => {
  if (!time24 || !time24.includes(':')) {
    return { hour: DEFAULT_HOUR, minute: DEFAULT_MINUTE, period: DEFAULT_PERIOD };
  }
  const [hours, minutes] = time24.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return { hour: DEFAULT_HOUR, minute: DEFAULT_MINUTE, period: DEFAULT_PERIOD };
  }

  let hour12 = hours % 12;
  if (hour12 === 0) hour12 = 12;
  const periodValue = hours >= 12 ? PERIOD_VALUES.PM : PERIOD_VALUES.AM;

  return {
    hour: String(hour12),
    minute: String(minutes).padStart(2, '0'),
    period: periodValue
  };
};

export const convertTo24Hour = (h, m, p) => {
  const hour24 = parseInt(h, 10);
  const minute60 = parseInt(m, 10);
  if (Number.isNaN(hour24) || Number.isNaN(minute60)) return '';
  if (hour24 < HOUR_MIN || hour24 > HOUR_MAX || minute60 < MINUTE_MIN || minute60 > MINUTE_MAX) return '';

  let hour24Value = hour24;
  const isPM = p.toUpperCase() === PERIOD_VALUES.PM.toUpperCase();
  if (isPM && hour24Value !== 12) hour24Value += 12;
  if (!isPM && hour24Value === 12) hour24Value = 0;

  return `${String(hour24Value).padStart(2, '0')}:${String(minute60).padStart(2, '0')}`;
};

/** API/form time → HH:mm, or null when absent or unparseable */
export const toFormTime = (timeStr) => {
  const trimmed = timeStr?.trim();
  if (!trimmed) return null;

  const twentyFourHour = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFourHour) {
    const hour = Number(twentyFourHour[1]);
    const min = Number(twentyFourHour[2]);
    if (hour >= 0 && hour <= 23 && min >= 0 && min <= 59) {
      return `${String(hour).padStart(2, '0')}:${twentyFourHour[2]}`;
    }

    return null;
  }

  const match = trimmed.match(TWELVE_HOUR_IN_STRING);
  if (!match) return null;

  const period = match[3].replace(/[.\s]/g, '');
  const converted = convertTo24Hour(match[1], match[2], period);

  return converted || null;
};


export const validateHour = (val) => {
  if (!val || val === '') return DEFAULT_HOUR_DISPLAY;
  let numVal = parseInt(val, 10);
  if (Number.isNaN(numVal) || numVal < HOUR_MIN) numVal = HOUR_MIN;
  if (numVal > HOUR_MAX) numVal = HOUR_MAX;

  return String(numVal).padStart(2, '0');
};

export const validateMinute = (val) => {
  if (!val || val === '') return DEFAULT_MINUTE_DISPLAY;
  let numVal = parseInt(val, 10);
  if (Number.isNaN(numVal) || numVal < MINUTE_MIN) numVal = MINUTE_MIN;
  if (numVal > MINUTE_MAX) numVal = MINUTE_MAX;

  return String(numVal).padStart(2, '0');
};

/**
 * Converts a wall-clock date/time in a specific IANA timezone to a UTC Date object.
 * Used in AssignmentUtils on save — the instructor picks a date, time, and timezone
 * in the form (e.g. "2026-03-12", "23:59", "America/Anchorage") and we convert it to
 * an absolute UTC moment before sending to the API as `dateDue`.
 *
 * If `timeStr` is omitted, defaults to the standard end-of-day form time (23:59).
 *
 * @example
 * wallClockToUTC("2026-03-12", "23:59", "Asia/Kolkata") → Date("2026-03-12T18:29:00.000Z")
 */
export const wallClockToUTC = (dateStr, timeStr, ianaTimezone) => {
  if (!dateStr || !ianaTimezone) return null;
  const dt = DateTime.fromISO(`${dateStr}T${toFormTime(timeStr) || DEFAULT_FORM_TIME}`, { zone: ianaTimezone });

  return dt.isValid ? dt.toUTC().toJSDate() : null;
};

/**
 * Per ISO 8601, the literal "T" separates date from time in a datetime string.
 * A string without "T" is a date-only value and carries no time information.
 * Used as a guard in utcToWallClock and formatDisplayTime to distinguish the two shapes.
 */
const isFullISOString = (str) => Boolean(str?.includes('T'));

/** Returns the browser's IANA timezone (e.g. "Asia/Kolkata"). */
export const getBrowserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Private: UTC ISO → wall-clock {date, time} in a given IANA timezone. Returns null for plain date strings. */
const utcToWallClock = (isoDateStr, ianaTimezone) => {
  if (!isFullISOString(isoDateStr) || !ianaTimezone) return null;
  const dt = DateTime.fromISO(isoDateStr, { zone: ianaTimezone });

  return dt.isValid ? { date: dt.toFormat('yyyy-MM-dd'), time: dt.toFormat('HH:mm') } : null;
};

/**
 * Converts a stored wall-clock date/time from the instructor's timezone to the browser's local wall-clock.
 * Used in AddEditAssignmentForm on edit so the form matches the dashboard tabular display.
 * Returns null when no timezone is stored (dashboard/API items — no conversion needed).
 *
 * @example wallClockToBrowserLocal("2026-01-01", "23:59", "Asia/Tokyo") → { date: "2026-01-01", time: "20:29" }
 */
export const wallClockToBrowserLocal = (date, time, fromTimezone) => {
  if (!date || !fromTimezone) return null;
  const utc = wallClockToUTC(date, time, fromTimezone);

  return utc ? utcToWallClock(utc.toISOString(), getBrowserTimezone()) : null;
};

/**
 * Private helper: finds the best matching timezone option for a given IANA key.
 *
 * Step 1 — exact match: looks for an option whose value === ianaKey.
 *   Works for most modern browsers that report standard IANA names (e.g. "Asia/Kolkata").
 *
 * Step 2 — offset fallback: if no exact match, compares UTC offsets instead.
 *   Needed because some environments (e.g. older Windows) report deprecated IANA aliases
 *   like "Asia/Calcutta" which isn't in our timezone list, but shares the same UTC offset
 *   (+330 min) as "Asia/Kolkata" which is — so we can still find the right option.
 *
 * Step 3: Step 2 — group fallback: ianaKey in opt.group (e.g. America/New_York in Eastern group).
 */
const findOptionByTimezone = (ianaKey, options) => {
  if (!ianaKey || !options?.length) return null;

  const exactMatch = options.find(opt => opt.value === ianaKey);
  if (exactMatch) return exactMatch;

  // Skip offset-only fallback: many zones share the same offset, so we’d pick the wrong one (e.g. CST instead of ET).
  // Group fallback uses IANA aliases to match the correct zone.
  const groupMatch = options.find(opt => opt.group?.includes(ianaKey));
  if (groupMatch) return groupMatch;
  let keyOffset;
  try {
    keyOffset = DateTime.now().setZone(ianaKey).offset;
  } catch {
    return null;
  }

  return options.find((opt) => {
    try {
      return DateTime.now().setZone(opt.value).offset === keyOffset;
    } catch {
      return false;
    }
  }) || null;
};

/**
 * Matches the browser's current IANA timezone to an option in the timezone dropdown list.
 * Used in AddEditAssignmentForm to pre-select the correct timezone.
 */
export const matchTimezoneToOptions = (timeZoneOptions) => {
  const match = findOptionByTimezone(getBrowserTimezone(), timeZoneOptions);

  return match?.value || null;
};

/**
 * Resolves the display abbreviation for a given IANA timezone key against the timezone list.
 * Used in DateTemplate to append the timezone label
 *
 * @example
 * getTzLabelFromOptions("America/New_York", tzList) → "ET"
 */
export const getTzLabelFromOptions = (ianaKey, timeZoneOptions) => {
  const match = findOptionByTimezone(ianaKey, timeZoneOptions);
  if (!match) return null;

  return match.abbr || match.offset || null;
};

/**
 * Formats a date string into a browser-local 12-hour time string.
 * Internal helper used only by formatDisplayTime. Accepts a full UTC ISO string
 * (e.g. "2024-09-05T18:29:00.000Z") or a local combined string (e.g. "2024-09-05T23:59").
 */
const getLocalTime = (isoDateStr) => {
  if (!isoDateStr) return '';

  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(new Date(isoDateStr));
};

/**
 * Combines a YYYY-MM-DD date and HH:mm time into a local wall-clock
 * datetime string (yyyy-MM-ddTHH:mm) for display.
 */
export const combineLocalDateTime = (dateStr, timeStr) => (
  dateStr ? `${dateStr}T${toFormTime(timeStr) || DEFAULT_FORM_TIME}` : null
);

/**
 * Resolves the correct 12-hour time string to display for an assignment.
 * Handles two shapes of input:
 *
 * 1. Plain date string (YYYY-MM-DD): used by the syllabus confirmation screen
 * 2. Full UTC ISO string (contains "T"): used by the dashboard/grid after API read-back
 */
export const formatDisplayTime = (date, time) => {
  if (!date) return '';
  const parsed = new Date(date);
  // we do not show the empty date, earlier it default took as 1970
  if (Number.isNaN(parsed.getTime())) return '';
  // Plain date string (YYYY-MM-DD): combine with the explicit time if provided,
  // or fall back to DEFAULT_FORM_TIME (23:59)
  if (!isFullISOString(date)) {
    return getLocalTime(`${date}T${time || DEFAULT_FORM_TIME}`);
  }

  // Full UTC ISO string: convert directly to local browser time.
  return getLocalTime(date);
};
